/**
 * How the workspace lists what the AI Analyst saved: readable titles, relative
 * dates, and re-runs of one request shown as versions of one card.
 *
 * Pure functions, so every rule the page applies can be tested without
 * rendering it.
 */
import type { DashboardSummary } from "../../types";

export type DashboardFilter = "all" | "draft" | "published" | "shared" | "hidden";
export type DashboardSort = "recent" | "name" | "panels";
export type TimeBucket = "Today" | "Yesterday" | "This week" | "This month" | "Earlier";

export const FILTERS: { id: DashboardFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "draft", label: "Drafts" },
  { id: "published", label: "Published" },
  { id: "shared", label: "Shared with me" },
  { id: "hidden", label: "Hidden" },
];

export const SORTS: { id: DashboardSort; label: string }[] = [
  { id: "recent", label: "Recently updated" },
  { id: "name", label: "Name" },
  { id: "panels", label: "Most panels" },
];

const CHART_LABELS: Record<string, string> = {
  bar: "Bar",
  line: "Line",
  pie: "Pie",
  scalar: "KPI",
  table: "Table",
};

const DAY = 86_400_000;

/**
 * A saved name as a person would write it.
 *
 * Agents sometimes name a dashboard like a file
 * (`project_workforce_performance_dashboard`). A name that already has spaces
 * was written for people and is left as it is.
 */
export function readableTitle(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return "Untitled";
  if (/\s/.test(trimmed) || !/[_-]/.test(trimmed)) return trimmed;
  const words = trimmed.replace(/[_-]+/g, " ").replace(/\s+/g, " ").trim().toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function chartLabel(chartType: string): string {
  return CHART_LABELS[chartType] ?? readableTitle(chartType);
}

const SECTION_LABELS: Record<string, string> = {
  headline: "Summary",
  kpi: "KPIs",
  findings: "Findings",
  table: "Table",
};

/** What a document holds, once each, in the order it first appears. */
export function outlineLabels(outline: string[] = []): string[] {
  const labels = outline.map((kind) => SECTION_LABELS[kind] ?? `${chartLabel(kind)} chart`);
  return [...new Set(labels)];
}

function startOfDay(value: Date): number {
  return new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime();
}

/** "3 h ago", "Yesterday", "4 days ago", then the date itself after a week. */
export function relativeTime(iso: string, now: Date = new Date()): string {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "";
  const seconds = Math.round((now.getTime() - then.getTime()) / 1000);
  if (seconds < 0) return then.toLocaleDateString();
  if (seconds < 60) return "Just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  const days = Math.round((startOfDay(now) - startOfDay(then)) / DAY);
  if (days === 0) return `${Math.floor(seconds / 3600)} h ago`;
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;
  return then.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

export function timeBucket(iso: string, now: Date = new Date()): TimeBucket {
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return "Earlier";
  const days = Math.round((startOfDay(now) - startOfDay(then)) / DAY);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  if (days < 7) return "This week";
  if (days < 30) return "This month";
  return "Earlier";
}

/**
 * What every saved item in the workspace has in common. Dashboards are named,
 * documents are titled; panels and sections are both how big it is.
 */
export interface SavedItem {
  id: string;
  contextId: string;
  updatedAt: string;
  name?: string;
  title?: string;
  description?: string;
  subject?: string;
  panelCount?: number;
  sectionCount?: number;
}

/** One request re-run several times: the newest run leads, the rest follow. */
export interface SavedGroup<T extends SavedItem> {
  key: string;
  title: string;
  latest: T;
  versions: T[];
}

export type DashboardGroup = SavedGroup<DashboardSummary>;

function savedTitle(item: SavedItem): string {
  return item.name ?? item.title ?? "";
}

function size(item: SavedItem): number {
  return item.panelCount ?? item.sectionCount ?? 0;
}

/**
 * Re-runs are matched on the readable title and the model they read, never the
 * title alone: the same question asked of two models is two answers.
 */
export function versionKey(item: SavedItem): string {
  const title = readableTitle(savedTitle(item))
    .toLowerCase()
    .replace(/\bvs\b\.?/g, "vs")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
  return `${item.contextId}::${title}`;
}

function newestFirst(a: SavedItem, b: SavedItem): number {
  return Date.parse(b.updatedAt) - Date.parse(a.updatedAt) || a.id.localeCompare(b.id);
}

export function groupVersions<T extends SavedItem>(items: T[]): SavedGroup<T>[] {
  const groups = new Map<string, T[]>();
  for (const item of items) {
    const key = versionKey(item);
    const group = groups.get(key);
    if (group) group.push(item);
    else groups.set(key, [item]);
  }
  return [...groups.entries()].flatMap(([key, versions]) => {
    const sorted = [...versions].sort(newestFirst);
    const latest = sorted[0];
    return latest
      ? [{ key, title: readableTitle(savedTitle(latest)), latest, versions: sorted }]
      : [];
  });
}

/** The filters the server does not apply. Hidden is a separate list it serves. */
export function matchesFilter(
  summary: { status: string; shared?: boolean },
  filter: DashboardFilter,
): boolean {
  if (filter === "draft") return summary.status === "draft";
  if (filter === "published") return summary.status !== "draft";
  if (filter === "shared") return Boolean(summary.shared);
  return true;
}

export function matchesSearch(item: SavedItem, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  const title = savedTitle(item);
  return [title, readableTitle(title), item.description ?? "", item.subject ?? ""]
    .some((text) => text.toLowerCase().includes(needle));
}

export function sortGroups<T extends SavedItem>(
  groups: SavedGroup<T>[],
  sort: DashboardSort,
): SavedGroup<T>[] {
  const sorted = [...groups];
  if (sort === "name") {
    sorted.sort((a, b) => a.title.localeCompare(b.title, undefined, { sensitivity: "base" }));
  } else if (sort === "panels") {
    sorted.sort((a, b) => size(b.latest) - size(a.latest) || newestFirst(a.latest, b.latest));
  } else {
    sorted.sort((a, b) => newestFirst(a.latest, b.latest));
  }
  return sorted;
}

/** Consecutive runs of groups under the bucket their newest version falls in. */
export function bucketGroups<T extends SavedItem>(
  groups: SavedGroup<T>[],
  now: Date = new Date(),
): { bucket: TimeBucket; groups: SavedGroup<T>[] }[] {
  const sections: { bucket: TimeBucket; groups: SavedGroup<T>[] }[] = [];
  for (const group of groups) {
    const bucket = timeBucket(group.latest.updatedAt, now);
    const last = sections[sections.length - 1];
    if (last && last.bucket === bucket) last.groups.push(group);
    else sections.push({ bucket, groups: [group] });
  }
  return sections;
}
